import React, { Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import GoogleCallback from "../features/auth/components/GoogleCallback";
import SignInMobile from "../features/auth/components/SignInMobile";
import SignInEmail from "../features/auth/components/SignInEmail";
import SignUpEmail from "../features/auth/components/SignUpEmail";
import VerifyEmail from "../features/auth/components/VerifyEmail";
import SetPassword from "../features/auth/components/SetPassword";
import AddMobileNumber from "../features/auth/components/AddMobileNumber";
import VerifyMobile from "../features/auth/components/VerifyMobile";
import AddUserName from "../features/auth/components/AddUserName";
import TermsAcceptance from "../features/auth/components/TermsAcceptance";
import ForgotPassword from "../features/auth/components/ForgotPassword";
import ResetPassword from "../features/auth/components/ResetPassword";
import ProtectedRoute from "./ProtectedRoute";
import Terms from "../features/legal/Terms";
import Privacy from "../features/legal/Privacy";
import Takedown from "../features/legal/Takedown";
const DashboardLayout = lazy(() =>
  import("../features/dashboard/pages/DashboardLayout")
);
const DashboardHome = lazy(() =>
  import("../features/dashboard/pages/DashboardHome")
);
const Orders = lazy(() => import("../features/orders/pages/Orders"));
const OrderDetail = lazy(() => import("../features/orders/pages/OrderDetail"));
const Cart = lazy(() => import("../features/orders/pages/Cart"));
const OrderBoard = lazy(() => import("../features/orders/pages/OrderBoard"));
const Recipes = lazy(() => import("../features/dashboard/pages/Recipes"));
const RecipeDetail = lazy(() =>
  import("../features/dashboard/pages/RecipeDetail")
);
const RecipeReviews = lazy(() =>
  import("../features/dashboard/pages/RecipeReviews")
);
const VendorDetail = lazy(() =>
  import("../features/dashboard/pages/VendorDetail")
);
const Settings = lazy(() => import("../features/dashboard/pages/Settings"));
const ProfileInfo = lazy(() =>
  import("../features/dashboard/pages/ProfileInfo")
);
const DeliveryOptions = lazy(() =>
  import("../features/dashboard/pages/DeliveryOptions")
);
const NotificationSettings = lazy(() =>
  import("../features/dashboard/pages/NotificationSettings")
);
const LanguageSettings = lazy(() =>
  import("../features/dashboard/pages/LanguageSettings")
);
const Notifications = lazy(() =>
  import("../features/dashboard/pages/Notifications")
);
const Vouchers = lazy(() => import("../features/dashboard/pages/Vouchers"));
const Support = lazy(() => import("../features/dashboard/pages/Support"));
const SupportContact = lazy(() => import("../features/dashboard/pages/SupportContact"));
const RateLimit = lazy(() => import("../features/error/pages/RateLimit"));

export default function AppRoutes() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const isLoading = useSelector((state) => state.auth.isLoading);
  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/"
            element={
              isLoading ? (
                <LoadingSpinner />
              ) : (
                <Navigate
                  to={isAuthenticated ? "/dashboard" : "/auth/sign-in"}
                  replace
                />
              )
            }
          />
          {/* Auth Routes */}
          <Route path="/auth/sign-in" element={<SignInMobile />} />
          <Route path="/auth/sign-in-with-email" element={<SignInEmail />} />
          <Route path="/auth/sign-up-with-email" element={<SignUpEmail />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/set-password" element={<SetPassword />} />
          <Route path="/auth/add-mobile-number" element={<AddMobileNumber />} />
          <Route path="/auth/verify-mobile-number" element={<VerifyMobile />} />
          <Route path="/auth/add-username" element={<AddUserName />} />
          <Route path="/auth/terms" element={<TermsAcceptance />} />

          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/success" element={<GoogleCallback />} />

          {/* Public Legal Pages */}
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/takedown" element={<Takedown />} />

          {/* Public Vendor Pages */}
          <Route path="/vendor/:id" element={<VendorDetail />} />
          {/* Short alias for sharing; will later switch to slug */}
          <Route path="/v/:id" element={<VendorDetail />} />

          {/* Protected Route */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/orders/board" element={<OrderBoard />} />
            <Route path="/orders/:id" element={<OrderDetail />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/vouchers" element={<Vouchers />} />
            <Route path="/support" element={<Support />} />
            <Route path="/support/contact" element={<SupportContact />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/recipe/:id" element={<RecipeDetail />} />
            <Route path="/recipe/:id/reviews" element={<RecipeReviews />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/profile" element={<ProfileInfo />} />
            <Route path="/delivery-options" element={<DeliveryOptions />} />
            <Route path="/notification-settings" element={<NotificationSettings />} />
            <Route path="/language-settings" element={<LanguageSettings />} />
          </Route>

          {/* Public error page */}
          <Route path="/error/429" element={<RateLimit />} />

          {/* Fallback Route */}
          <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
